import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, AlertCircle, Loader2, XCircle, Image as ImageIcon } from "lucide-react";
import { getCustomerDisplayName } from "@/lib/formatters";
import { toast } from "sonner";

interface ProofData {
  id: string;
  file_name: string;
  version: number;
  status: string;
  sent_at: string;
  approved_at: string | null;
  revision_notes: string | null;
  image_url: string;
  orders: {
    order_number: string;
    customers: {
      first_name: string | null;
      last_name: string | null;
      company_name: string | null;
      type: string;
    };
  };
}

export default function ProofApproval() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [proof, setProof] = useState<ProofData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revisionNotes, setRevisionNotes] = useState("");
  const [completed, setCompleted] = useState<"approved" | "revision_requested" | null>(null);

  useEffect(() => {
    if (token) {
      fetchProof();
    }
  }, [token]);

  const fetchProof = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-proof?token=${token}`
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Vedosta ei löytynyt");
      }
      
      setProof(data.proof);
      
      if (data.proof.status === "approved") {
        setCompleted("approved");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Tuntematon virhe");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: "approve" | "request_revision") => {
    if (action === "request_revision" && !revisionNotes.trim()) {
      toast.error("Muutospyyntö puuttuu", {
        description: "Kirjoita mitä muutoksia haluat vedokseen",
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-proof`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token,
            action,
            revision_notes: action === "request_revision" ? revisionNotes : undefined,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Toiminto epäonnistui");
      }

      setCompleted(action === "approve" ? "approved" : "revision_requested");
      toast.success(action === "approve" ? "Vedos hyväksytty!" : "Muutospyyntö lähetetty", {
        description: action === "approve" 
          ? "Kiitos hyväksynnästä. Työ jatkuu." 
          : "Otamme huomioon muutospyyntönne.",
      });
    } catch (err) {
      toast.error("Virhe", {
        description: err instanceof Error ? err.message : "Tuntematon virhe",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getCustomerName = () => {
    if (!proof) return "";
    return getCustomerDisplayName(proof.orders.customers);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
            <p className="text-muted-foreground">Ladataan vedosta...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 mx-auto text-destructive mb-4" />
            <h1 className="text-xl font-semibold mb-2">Vedosta ei löytynyt</h1>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-4">
              Linkki on saattanut vanhentua tai vedos on jo käsitelty.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            {completed === "approved" ? (
              <>
                <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
                <h1 className="text-xl font-semibold mb-2">Vedos hyväksytty!</h1>
                <p className="text-muted-foreground">
                  Kiitos hyväksynnästä. Työ jatkuu suunnitellusti.
                </p>
              </>
            ) : (
              <>
                <AlertCircle className="h-16 w-16 mx-auto text-orange-500 mb-4" />
                <h1 className="text-xl font-semibold mb-2">Muutospyyntö lähetetty</h1>
                <p className="text-muted-foreground">
                  Olemme vastaanottaneet muutospyyntönne ja otamme teihin yhteyttä uuden vedoksen valmistuttua.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">Vedoksen tarkastus</h1>
          <p className="text-muted-foreground">
            Tilaus: <span className="font-medium">{proof?.orders.order_number}</span>
          </p>
          {getCustomerName() && (
            <p className="text-sm text-muted-foreground">
              Asiakas: {getCustomerName()}
            </p>
          )}
        </div>

        {/* Proof image */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Vedos (versio {proof?.version})
            </CardTitle>
            <CardDescription>
              Tarkista vedos ja hyväksy se tai pyydä muutoksia
            </CardDescription>
          </CardHeader>
          <CardContent>
            {proof?.image_url ? (
              <div className="relative overflow-hidden rounded-lg border">
                <img
                  src={proof.image_url}
                  alt="Vedos"
                  className="w-full h-auto"
                />
              </div>
            ) : (
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                <p className="text-muted-foreground">Kuvaa ei voitu ladata</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Hyväksy tai pyydä muutoksia</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Approve button */}
            <div>
              <Button 
                size="lg" 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => handleAction("approve")}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Hyväksy vedos
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">
                Hyväksymällä vedoksen vahvistat, että työ voidaan toteuttaa kuvan mukaisesti.
              </p>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">tai</span>
              </div>
            </div>

            {/* Revision request */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="revision-notes">Pyydä muutoksia</Label>
                <Textarea
                  id="revision-notes"
                  placeholder="Kirjoita tähän mitä muutoksia haluaisit vedokseen..."
                  value={revisionNotes}
                  onChange={(e) => setRevisionNotes(e.target.value)}
                  rows={4}
                />
              </div>
              <Button 
                variant="outline" 
                size="lg"
                className="w-full"
                onClick={() => handleAction("request_revision")}
                disabled={submitting || !revisionNotes.trim()}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <AlertCircle className="h-4 w-4 mr-2" />
                )}
                Lähetä muutospyyntö
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-muted-foreground">
          <p>Tämä sivu on luotu automaattisesti vedoshyväksyntää varten.</p>
          <p>Älä jaa tätä linkkiä muille.</p>
        </div>
      </div>
      
    </div>
  );
}
